import { Message } from "./Messages"
import SI, { IslandState, Outcome, Play, Player } from "./SI"

export default class Parsing {

	public static findGameId = (games: BggGame[]): number | null => {
		for (const game of games) {
			if (game.name === 'Spirit Island') {
				return game.id
			}
		}

		return null
	}

	public static findGameOutcome = (bgPlay: BggPlay): Outcome => {
		const comments = (bgPlay.comments || '').toLowerCase()
		if (comments.indexOf('victory at terror level iii') === 0 || comments.indexOf('victory at terror level 3') === 0) {
			return {
				victory: true,
				type: 'Terror level III',
			}
		}
		if (comments.indexOf('victory at terror level ii') === 0 || comments.indexOf('victory at terror level 2') === 0) {
			return {
				victory: true,
				type: 'Terror level II',
			}
		}
		if (comments.indexOf('victory at terror level i') === 0 || comments.indexOf('victory at terror level 1') === 0) {
			return {
				victory: true,
				type: 'Terror level I',
			}
		}
		if (comments.indexOf('fear victory') === 0 || comments.indexOf('terror victory') === 0) {
			return {
				victory: true,
				type: 'Fear',
			}
		}
		if (bgPlay.playerScores[0].winner) {
			// A won game with no victory condition.
			if (bgPlay.playDate < '2021-02-22') {
				// throw rejectPlay(bgPlay)
				return {
					victory: true,
					type: '[Unknown victory]',
				}
			}
			throw Parsing.rejectPlay(bgPlay, `No victory condition: ${bgPlay.comments}`)
		}

		// A loss
		if (SI.blightLosses.find(lossCondition => comments.indexOf(lossCondition.toLowerCase()) === 0)) {
			return {
				victory: false,
				type: 'Blight',
			}
		}
		if (SI.adversaryLossConditions.find(lossCondition => comments.indexOf(lossCondition.toLowerCase()) === 0)) {
			return {
				victory: false,
				type: 'Adversary Loss Condition',
			}
		}
		if (SI.spiritDestroyedLossConditions.find(lossCondition => comments.indexOf(lossCondition.toLowerCase()) === 0)) {
			return {
				victory: false,
				type: 'Spirit Destroyed',
			}
		}
		if (SI.timeOutLosses.find(lossCondition => comments.indexOf(lossCondition.toLowerCase()) === 0)) {
			return {
				victory: false,
				type: 'Time out',
			};
		}

		if (bgPlay.playDate < '2019-12-23') {
			// if (si.excludeOldPlays) {
			// throw rejectPlay(bgPlay)
			return {
				victory: false,
				type: '[Unknown loss]',
			}
		}
		throw Parsing.rejectPlay(bgPlay, `Can't find loss condition in: ${bgPlay.comments}`)
	}

	public static findIslandState = (bgPlay: BggPlay): IslandState | null => {
		const comments = (bgPlay.comments || '').toLowerCase()

		for (const healthyIsland of SI.healthyIslands) {
			if (comments.indexOf(healthyIsland.toLowerCase()) > -1) {
				return {
					healthy: true,
					blightCards: [],
				}
			}
		}

		const blightIndicator = 'blight card was '
		const blightIndicatorPos = comments.indexOf(blightIndicator)
		if (blightIndicatorPos > -1) {
			const blightText = comments.substr(blightIndicatorPos + blightIndicator.length)
			for (const blightCard of SI.blightCards) {
				if (blightText.indexOf(blightCard.toLowerCase()) === 0) {
					return {
						healthy: false,
						blightCards: [blightCard],
					}
				}
			}

			for (const blightCard of SI.stillHealthyCards) {
				if (blightText.indexOf(blightCard.toLowerCase()) === 0) {
					return {
						healthy: true,
						blightCards: [blightCard],
					}
				}
			}

			throw Parsing.rejectPlay(bgPlay, `Unknown Blight card: "${blightText.substr(0, 50)}..." in ${bgPlay.comments}`)
		}

		const multiBlightIndicator = 'blight cards were '
		const multiBlightIndicatorPos = comments.indexOf(multiBlightIndicator)
		const islandState: IslandState = {
			healthy: true,
			blightCards: [],
		}
		if (multiBlightIndicatorPos > -1) {
			const endOfLinePos = comments.indexOf('\n', multiBlightIndicatorPos)
			const blightCardNames = comments.substring(multiBlightIndicatorPos + multiBlightIndicator.length, endOfLinePos).trim().replace('.', '').split(', ')
			for (const blightCardName of blightCardNames) {
				const blightCard = SI.blightCards.find(bc => bc.toLowerCase() === blightCardName)
				const stillHealthyCard = SI.stillHealthyCards.find(bc => bc.toLowerCase() === blightCardName)

				if (blightCard) {
					islandState.healthy = false
					islandState.blightCards.push(blightCard)
					return islandState
				} else if (stillHealthyCard) {
					islandState.blightCards.push(stillHealthyCard)
				} else {
					throw Parsing.rejectPlay(bgPlay, `Unknown Blight card (Still Healthy?): ${blightCardName}`)
				}
			}

			return islandState
		}

		if (bgPlay.playDate < '2020-03-15') {
			// throw rejectPlay(bgPlay)
			return null
		}
		throw Parsing.rejectPlay(bgPlay, `No Blight state: ${bgPlay.comments}`)
	}

	public static processData = (data: string) => {
		let bgStatsJson: BggJson
		try {
			bgStatsJson = JSON.parse(data)
		} catch (error) {
			throw new Error(`Invalid file format`)
		}

		if (!bgStatsJson.games || !bgStatsJson.plays) {
			throw new Error(`Invalid file`)
		}

		const gameId = Parsing.findGameId(bgStatsJson.games)
		if (gameId === undefined) {
			throw new Error(`Can't find a game called Spirit Island`)
		}

		// Get all the players
		const players: Player[] = []
		for (const player of bgStatsJson.players) {
			players[player.id] = {
				name: player.name,
				playCount: 0,
			}
		}

		let siPlays = bgStatsJson.plays.filter(bgPlay => bgPlay.gameRefId === gameId)
		siPlays = siPlays.filter(bgPlay => bgPlay.playerScores) // Must have players

		// Get all the locations
		const locationById: Map<number, string> = new Map()
		for (const location of bgStatsJson.locations) {
			locationById.set(location.id, location.name)
		}

		// Sort by date descending
		siPlays.sort((a, b) => (a.playDate > b.playDate) ? -1 : (a.playDate < b.playDate) ? 1 : 0)

		const parsedPlays: Play[] = []
		const parseMessages: Message[] = []
		let playNumber: number = siPlays.length
		for (const bgPlay of siPlays) {
			try {
				parsedPlays.push(Parsing.parsePlay(bgPlay, players, locationById, playNumber))
				playNumber--
			} catch (error: any) {
				if (error.message) {
					parseMessages.push({
						text: error.message,
						type: 'warning',
					})
				}
			}
		}

		if (parsedPlays.length > 0) {
			parseMessages.push({
				text: `Parsed ${parsedPlays.length} plays`,
				type: 'success',
			})
		}

		return {
			messages: parseMessages,
			plays: parsedPlays,
			difficulties: [...new Set(parsedPlays.filter(play => play.difficulty !== null).map(play => play.difficulty) as number[])],
			locations: [...new Set(parsedPlays.map(play => play.location))],
			maxSpirits: Math.max(...parsedPlays.map(play => play.spirits.length)),
			layouts: [...new Set(parsedPlays.filter(play => !!play.layout).map(play => play.layout) as string[])],
		}
	}

	public static parsePlay = (bgPlay: BggPlay, players: Player[], locationById: Map<number, string>, number: number): Play => {
		if (!bgPlay.locationRefId) {
			throw Parsing.rejectPlay(bgPlay, `No location`)
		}

		const location = locationById.get(bgPlay.locationRefId)
		if (!location) {
			throw Parsing.rejectPlay(bgPlay, `Invalid location`)
		}

		const parsed: Play = {
			additionalBoards: 0,
			adversaries: [],
			comments: bgPlay.comments,
			dateTimeString: bgPlay.playDate,
			difficulty: null,
			durationMins: bgPlay.durationMin,
			islandState: Parsing.findIslandState(bgPlay),
			layout: 'Balanced',
			location,
			number,
			outcome: Parsing.findGameOutcome(bgPlay),
			players: [],
			scenario: null,
			score: this.evaluateScore(bgPlay, bgPlay.playerScores[0].score),
			spirits: [],
			turns: bgPlay.rounds,
		}

		let playParams
		if (!bgPlay.board) {
			throw Parsing.rejectPlay(bgPlay, `No parameters`)
		}

		playParams = bgPlay.board.split(SI.separator)
		for (const param of playParams) {
			const parts = param.split(' ')
			if (parts.length > 1 && SI.adversaries.findIndex(adversary => adversary.name === param.substring(0, param.lastIndexOf(' '))) > -1) {
				parsed.adversaries.push({
					name: param.substring(0, param.lastIndexOf(' ')),
					level: parseInt(parts[parts.length - 1], 10),
				})
				if (parsed.adversaries.length > 2) {
					throw Parsing.rejectPlay(bgPlay, `Too many adversaries: ${bgPlay.board}`)
				}
			} else if (parts.length === 2 && parts[0] === 'Difficulty') {
				parsed.difficulty = parseFloat(parts[1])
			} else if (param.indexOf('Second Wave') === 0) {
				parsed.scenario = param
			} else if (param.substring(0, 10) === 'Scenario: ' && SI.scenarios.indexOf(param.substring(10)) > -1) {
				parsed.scenario = param.substring(10)
			} else if (param === 'Thematic Map') {
				parsed.layout = 'Thematic'
			} else if (param === '+ 1 Board') {
				parsed.additionalBoards = 1
			} else if (param === '+ 2 Boards') {
				parsed.additionalBoards = 2
			} else if (param === '+ 3 Boards') {
				parsed.additionalBoards = 3
			} else if (param === '+ 4 Boards') {
				parsed.additionalBoards = 4
			} else if (param === '+ 5 Boards') {
				parsed.additionalBoards = 5
			} else if (param === '+ 6 Boards') {
				parsed.additionalBoards = 6
			} else if (param.substring(0, 8) === 'Layout: ') {
				parsed.layout = param.substring(8);
			} else {
				throw Parsing.rejectPlay(bgPlay, `Unknown playParam: ${param}`)
			}
		}

		for (const playerScore of bgPlay.playerScores) {
			const player = players[playerScore.playerRefId]
			parsed.players.push(player)
			player.playCount++
			const roles = playerScore.role.toLowerCase().split(SI.separator)
			for (const role of roles) {
				const spirit = SI.spirits.find(s => s.name.toLowerCase() === role)
				const aspect = SI.aspects.find(a => 'aspect: ' + a.name.toLowerCase() === role)

				if (spirit) {
					parsed.spirits.push({
						spirit,
						aspect: null,
						player: player,
					})
				} else if (aspect) {
					if (parsed.spirits.length === 0) {
						throw Parsing.rejectPlay(bgPlay, `${playerScore.role} without Spirit`)
					}
					const lastPlayedSpirit = parsed.spirits[parsed.spirits.length - 1]
					if (aspect.spirit.name !== lastPlayedSpirit.spirit.name) {
						throw Parsing.rejectPlay(bgPlay, `${lastPlayedSpirit.spirit.name} can't have ${aspect.name}`)
					}
					if (lastPlayedSpirit.aspect) {
						throw Parsing.rejectPlay(bgPlay, `${lastPlayedSpirit.spirit.name} can't have ${aspect.name} and ${playerScore.role}`)
					}
					lastPlayedSpirit.aspect = aspect.name
				} else {
					throw Parsing.rejectPlay(bgPlay, `Unknown Spirit: ${role}`)
				}
			}
		}

		return parsed
	}

	public static rejectPlay = (bgPlay: BggPlay, reason: string | null): Error => {
		if (reason) {
			return new Error(`${bgPlay.playDate}: ${reason}`)
		}
		return new Error()
	}

	private static evaluateScore(bgPlay: BggPlay, scoreExpression: string | null): number | null {
		if (scoreExpression === null || scoreExpression === undefined || scoreExpression === '') {
			return null
		}


		if (!scoreExpression.match(/^[ \d+-]*$/)) {
			throw Parsing.rejectPlay(bgPlay, `Invalid score: "${scoreExpression}"`)
		}

		let total = 0
		const segments = scoreExpression.match(/[+-]*(\.\d+|\d+(\.\d+)?)/g) || [];

		for (const segment of segments) {
			if (segment) {
				total += parseFloat(segment)
			}
		}

		return total;
	}
}

interface BggGame {
	id: number
	name: string
}

interface BggJson {
	games: BggGame[]
	locations: BggLocation[]
	plays: BggPlay[]
	players: BggPlayer[]
}

interface BggLocation {
	id: number
	name: string
}

interface BggPlay {
	board: string | null
	comments: string
	durationMin: number | null
	gameRefId: number
	locationRefId: number | null
	rounds: number | null
	playDate: string
	playerScores: {
		playerRefId: number
		role: string
		score: string | null
		winner: boolean
	}[]
}

interface BggPlayer {
	id: number
	name: string
}

export interface ParseResults {
	difficulties: number[]
	layouts: string[]
	locations: string[]
	maxSpirits: number
	messages: Message[]
	plays: Play[]
}
